import React, { useState } from 'react';

import { acknowledgeCommunityMessage } from '../../../backendServices/communityManagment/api';
import { CommunityMessagesState } from './useCommunityMessages';

/**
 * Ce que voit le destinataire d'un message de la communauté.
 *
 * Volontairement dans la langue visuelle de TaskManager, son voisin dans le
 * bandeau : un bouton qui ne se montre QUE s'il a quelque chose à dire, et qui
 * déroule le détail au clic. C'est ce que demandait la commande — « comme pour
 * l'upload » — et c'est aussi ce qui évite d'ajouter un troisième vocabulaire de
 * notification à une application qui en a déjà deux.
 *
 * Les styles sont en ligne pour la même raison : TaskManager est écrit ainsi,
 * et deux boutons côte à côte qui ne se ressembleraient pas se remarqueraient
 * plus que le message lui-même.
 */

/** Le rouge des messages « Prioritaire », le même que dans le formulaire d'envoi. */
export const ROUGE_PRIORITAIRE = '#b91c1c';

const formatDate = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  });
};

interface CommunityMessageManagerProps {
  state : CommunityMessagesState;
  /** À qui attribuer l'accusé de lecture envoyé au serveur. */
  adminId : number | null;
}

const CommunityMessageManager = ({ state, adminId }: CommunityMessageManagerProps) => {
  const [isOpen        , setIsOpen        ] = useState(false);
  // Tous les messages sont dépliés par défaut ; on ne garde que ceux qu'on a
  // explicitement repliés.
  const [collapsedIds  , setCollapsedIds  ] = useState<Set<number>>(new Set());

  const { unread, readIds, markRead } = state;

  // Un message « Prioritaire » teinte le bouton lui-même : il doit se voir sans
  // ouvrir le panneau.
  const aUnPrioritaire = unread.some(m => m.priority === true);

  const toggleExpanded = (id: number) => {
    setCollapsedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); } else { next.add(id); }
      return next;
    });
  };

  const handleRead = (id: number) => {
    markRead(id);
    // L'accusé serveur est distinct du rangement local : c'est lui qui
    // alimente le badge « Lu » vu par l'auteur. Muet en cas d'échec, cf.
    // acknowledgeCommunityMessage.
    acknowledgeCommunityMessage(id, adminId);
    // Le dernier message rangé referme le panneau : le laisser ouvert et vide
    // donnerait l'impression d'avoir perdu quelque chose. Un prioritaire, lui,
    // reste affiché après lecture : le panneau n'est jamais vide tant qu'il y en a.
    const restants = unread.filter(m => m.id !== id && m.priority !== true);
    if (restants.length === 0 && !aUnPrioritaire) setIsOpen(false);
  };

  // Rien à dire : le bouton n'existe pas. Même règle que le gestionnaire
  // d'envois, qui ne s'affiche pas quand il n'y a aucun envoi.
  if (unread.length === 0) return null;

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          display        : 'flex',
          alignItems     : 'center',
          gap            : '8px',
          padding        : '12px 16px',
          backgroundColor: aUnPrioritaire ? ROUGE_PRIORITAIRE : '#4c1d95',
          color          : 'white',
          border         : 'none',
          borderRadius   : '8px',
          boxShadow      : '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
          cursor         : 'pointer',
          fontWeight     : '500',
          fontSize       : '14px',
          transition     : 'background-color 0.2s',
        }}
      >
        <span>
          {unread.length > 1
            ? `${unread.length} messages de Kleon.TV`
            : 'Messages de Kleon.TV'}
        </span>
        <span style={{ fontSize: '18px' }}>{isOpen ? '▼' : '▲'}</span>
      </button>

      {isOpen && (
        <div style={{
          position       : 'absolute',
          top            : '100%',
          right          : 0,
          marginTop      : '8px',
          width          : '420px',
          backgroundColor: '#4c1d95',
          border         : '1px solid #e5e7eb',
          borderRadius   : '8px',
          boxShadow      : '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
          overflow       : 'hidden',
          zIndex         : 50,
        }}>
          <div style={{ maxHeight: '384px', overflowY: 'auto' }}>
            {unread.map(message => {
              const estOuvert    = !collapsedIds.has(message.id);
              const prioritaire  = message.priority === true;
              const dejaLu       = prioritaire && readIds.includes(message.id);

              return (
                <div
                  key={message.id}
                  style={{
                    padding        : '16px',
                    borderBottom   : '1px solid #f3f4f6',
                    // Le prioritaire, à sa place dans la suite des messages, mais
                    // en rouge : c'est la couleur qui le distingue, pas la position.
                    backgroundColor: prioritaire ? ROUGE_PRIORITAIRE : undefined,
                  }}
                >
                  <div
                    onClick={() => toggleExpanded(message.id)}
                    style={{
                      display       : 'flex',
                      justifyContent: 'space-between',
                      alignItems    : 'baseline',
                      gap           : '8px',
                      cursor        : 'pointer',
                    }}
                  >
                    <span style={{ fontWeight: 600, color: 'white', fontSize: '14px' }}>
                      {prioritaire && (
                        <span style={{
                          display        : 'inline-block',
                          marginRight    : '8px',
                          padding        : '1px 6px',
                          borderRadius   : '4px',
                          backgroundColor: 'white',
                          color          : ROUGE_PRIORITAIRE,
                          fontSize       : '10px',
                          fontWeight     : 700,
                          letterSpacing  : '0.5px',
                          verticalAlign  : '1px',
                        }}>
                          PRIORITAIRE
                        </span>
                      )}
                      {message.title}
                    </span>
                    <span style={{ fontSize: '12px', color: 'white', flexShrink: 0 }}>
                      {formatDate(message.creationDate)}
                    </span>
                  </div>

                  {estOuvert && (
                    <>
                      {/* `pre-wrap` : le texte est saisi dans une zone multi-ligne, ses
                          retours à la ligne font partie de ce qui a été écrit. */}
                      <div style={{
                        fontSize  : '14px',
                        color     : 'white',
                        whiteSpace: 'pre-wrap',
                        wordBreak : 'break-word',
                        marginTop : '8px',
                      }}>
                        {message.message}
                      </div>

                      <div style={{ display: 'flex', gap: '8px', marginTop: '12px', justifyContent: 'flex-end', alignItems: 'center' }}>
                        {/* Un prioritaire reste là après lecture : l'accusé part au
                            serveur, et le bouton laisse place à « Lu ». */}
                        {dejaLu ? (
                          <span style={{ fontSize: '12px', color: 'white', opacity: 0.85 }}>
                            Lu — reste affiché jusqu'à son retrait
                          </span>
                        ) : (
                        <button
                          onClick={() => handleRead(message.id)}
                          style={{
                            padding        : '6px 12px',
                            fontSize       : '12px',
                            borderRadius   : '6px',
                            border         : 'none',
                            cursor         : 'pointer',
                            backgroundColor: '#e5e7eb',
                            color          : '#374151',
                          }}
                        >
                          J'ai lu
                        </button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default CommunityMessageManager;
